import { useMutation } from '@apollo/client/react';
import { type ControlledTextField } from '@drycut/controlled-components';
import { ValidatePhoneNumberDocument } from '@drycut/graphql';
import { DebouncedTextField, FormHelpText, Stack } from '@drycut/ui-components';
import { Controller, useFormContext } from 'react-hook-form';

import { useCheckoutStore } from './BookingCheckout/checkoutStore';

type Props = {
  onSuccessfulValidation?: () => void;
  onValidationError: () => void;
} & Omit<React.ComponentProps<typeof ControlledTextField>, 'label' | 'name' | 'value'>;

export function ControlledValidationPhoneField({
  onSuccessfulValidation,
  onValidationError,
  ...props
}: Props): React.ReactElement {
  const { trigger, setValue, setError, clearErrors } = useFormContext();
  const manual = useCheckoutStore((state) => state.manual);
  const [validatePhone, { loading }] = useMutation(ValidatePhoneNumberDocument);
  const phoneRegex =
    /^(\+{0,})(\d{0,})([(]{1}\d{1,3}[)]{0,}){0,}(\s?\d+|\+\d{2,3}\s{1}\d+|\d+){1}[\s|-]?\d+([\s|-]?\d+){1,2}(\s){0,}$/gm;

  const defaultErrorMessage = 'Diese Telefonnummer ist ungültig';

  // Triggered when the user stops typing for 500ms
  const handleDebouncedChange = (newValue?: string) => {
    setValue('phone', newValue);
    trigger('phone', { shouldFocus: true });

    if (newValue && newValue.length >= 10) {
      validateNumber(newValue);
    }
  };

  const validateNumber = async (phoneNumber: string) => {
    const { data } = await validatePhone({ variables: { input: { phoneNumber } } });

    const validation = data?.validatePhoneNumber;

    if (!validation?.valid) {
      setError('phone', { type: 'manual', message: defaultErrorMessage });
      onValidationError();
    } else {
      clearErrors('phone');
      onSuccessfulValidation?.();
    }
  };

  return (
    <Controller
      name="phone"
      rules={{
        required: defaultErrorMessage,
        minLength: { value: 10, message: defaultErrorMessage },
        pattern: { value: phoneRegex, message: defaultErrorMessage },
      }}
      render={({
        field: { name, ref, value, onBlur, disabled },
        fieldState: { invalid, error },
      }) => (
        <Stack direction="col" gap={1} className="relative">
          <DebouncedTextField
            type="tel"
            align="left"
            autoComplete={manual ? 'off' : 'tel'}
            label=""
            placeholder="Telefonnummer"
            loading={loading}
            onDebouncedChange={(value) => handleDebouncedChange(value)}
            invalid={invalid}
            name={name}
            ref={ref}
            value={value}
            onBlur={onBlur}
            disabled={disabled}
            {...props}
          />
          {error?.message && <FormHelpText error={invalid}>{error?.message}</FormHelpText>}
        </Stack>
      )}
    />
  );
}
